import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from './useLanguage';

interface Category {
  id: string;
  slug: string;
  name: string;
  name_translations: {
    fr?: string;
    en?: string;
    ln?: string;
    sw?: string;
  } | null;
}

// Cache des catégories pour éviter les requêtes multiples
let categoriesCache: Category[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes (augmenté)
let cachePromise: Promise<Category[]> | null = null;

// Précharger les catégories au démarrage de l'application
const preloadCategories = async (): Promise<Category[]> => {
  if (cachePromise) return cachePromise;
  
  cachePromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, slug, name, name_translations')
        .order('name');

      if (error) throw error;

      // Cast avec vérification de type pour éviter erreur TypeScript
      categoriesCache = data ? (data as unknown as Category[]) : [];
      cacheTimestamp = Date.now();
      
      // Vérifier que toutes les catégories ont des traductions
      const missingTranslations = categoriesCache.filter(
        cat => !cat.name_translations || 
               !cat.name_translations.fr || 
               !cat.name_translations.en
      );
      
      if (missingTranslations.length > 0) {
        console.warn(
          '⚠️ CATÉGORIES SANS TRADUCTIONS COMPLÈTES:',
          missingTranslations.map(c => c.slug).join(', '),
          '\nExécute le script fix-all-categories-final.sql dans Supabase!'
        );
      }
      
      return categoriesCache;
    } catch (error) {
      console.error('Error preloading categories:', error);
      cachePromise = null;
      return [];
    }
  })();
  
  return cachePromise;
};

// Démarrer le préchargement immédiatement
preloadCategories();

// Fonction publique pour forcer le rechargement (invalider le cache)
export const reloadCategoriesCache = async (): Promise<void> => {
  categoriesCache = null;
  cacheTimestamp = 0;
  cachePromise = null;
  await preloadCategories();
  console.log('✅ Cache des catégories rechargé avec succès');
};

export const useCategoryTranslation = (categorySlug: string | null) => {
  const { language } = useLanguage();
  const [categoryName, setCategoryName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categorySlug) {
      setCategoryName('');
      setLoading(false);
      return;
    }

    const fetchCategoryName = async () => {
      try {
        // Attendre que le préchargement soit terminé
        await preloadCategories();
        
        // Chercher dans le cache
        if (categoriesCache) {
          const category = categoriesCache.find(cat => cat.slug === categorySlug);
          if (category) {
            const translatedName = category.name_translations?.[language as 'fr' | 'en' | 'ln' | 'sw'] || category.name;
            
            // Avertir si la traduction manque
            if (!category.name_translations?.[language as 'fr' | 'en' | 'ln' | 'sw']) {
              console.warn(`⚠️ Traduction manquante pour catégorie "${categorySlug}" en langue "${language}"`);
            }
            
            setCategoryName(translatedName);
            setLoading(false);
            return;
          }
        }

        // Si pas trouvé dans le cache, fallback sur le nom formaté
        console.warn(`⚠️ Catégorie "${categorySlug}" non trouvée dans le cache. Utilisation du fallback.`);
        setCategoryName(formatSlugAsFallback(categorySlug));
      } catch (err) {
        console.error('Error in useCategoryTranslation:', err);
        setCategoryName(formatSlugAsFallback(categorySlug));
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryName();
  }, [categorySlug, language]);

  return { categoryName, loading };
};

// Hook pour charger toutes les catégories une fois
export const useAllCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await preloadCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  return { categories, loading };
};

// Fonction helper pour formater un slug en fallback
const formatSlugAsFallback = (slug: string): string => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Hook pour obtenir le nom traduit d'une catégorie depuis le cache
export const getCategoryNameFromCache = (slug: string, language: string): string => {
  if (!categoriesCache) return formatSlugAsFallback(slug);
  
  const category = categoriesCache.find(cat => cat.slug === slug);
  if (!category) return formatSlugAsFallback(slug);
  
  return category.name_translations?.[language as 'fr' | 'en' | 'ln' | 'sw'] || category.name;
};
